import KODIAK_FARM_ABI from 'abis/KodiakFarm.json'
import { ButtonLight, ButtonPrimary } from 'components/Button'
import { LightCard } from 'components/Card'
import { AutoColumn } from 'components/Column'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import CurrencyLogo from 'components/CurrencyLogo'
import FiatValue from 'components/FiatValue'
import { RowBetween, RowFixed } from 'components/Row'
import Slider from 'components/Slider'
import { TruncatedText } from 'components/swap/styleds'
import { MouseoverTooltip } from 'components/Tooltip'
import TransactionConfirmationModal, { ConfirmationModalContent } from 'components/TransactionConfirmationModal'
import { useToken } from 'hooks/Tokens'
import { ApprovalState, useApproveCallback } from 'hooks/useApproveCallback'
import { useConnection } from 'hooks/useConnection'
import { useCalculationEarnings, useKodiakFarmApr } from 'hooks/useKodiakFarm'
import { useApiTokenValue } from 'hooks/useTokenPrice'
import { useWriteContract } from 'hooks/useTransaction'
import useWaitForTransactionReceipt from 'hooks/useWaitForTransactionReceipt'
import useCurrencyBalance from 'lib/hooks/useCurrencyBalance'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import { useCallback, useContext, useMemo, useState } from 'react'
import { Text } from 'rebass'
import { useToggleWalletModal } from 'state/application/hooks'
import useInputValue from 'state/farms/hooks/useInputValue'
import useLockTimeValue, { useLockTimeMultiplier } from 'state/farms/hooks/useLockTimeValue'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import { ThemeContext } from 'styled-components'
import { Separator, ThemedText } from 'theme'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import formatMoney from 'utils/formatMoney'
import { Abi, Address, Hash } from 'viem'

import Column from '../../../components/Column/index'
import { REFETCH_POLICY_FAST } from '../../../config/RefetchPolicies'
import { useFarm } from './context/FarmContext'
import EarningsCalculationView from './EarningsCalculationView'
import { Dots, LockTimePositionHeader } from './styleds'
const ONE_DAY = 86400

const DepositView = () => {
  const theme = useContext(ThemeContext)
  const [showConfirm, setShowConfirm] = useState<boolean>(false)
  const [transactionState, setTransactionState] = useState({
    attemptingTxn: false,
    errorMessage: undefined as string | undefined,
    txHash: undefined as string | undefined,
    transactionWaiting: false,
  })

  const farm = useFarm()
  const currency = useToken(farm.stakingToken)
  const { account, wagmiPublicClient } = useConnection()
  const { writeContractAsync } = useWriteContract()
  const addTransaction = useTransactionAdder()
  const waitForTransactionReceipt = useWaitForTransactionReceipt()
  const tokenBalance = useCurrencyBalance(account, currency || undefined, REFETCH_POLICY_FAST)
  const [amount, setAmount] = useInputValue()
  const toggleWalletModal = useToggleWalletModal()
  const currencyAmount = useMemo(() => tryParseCurrencyAmount(amount, currency || undefined), [amount])
  const isValid = !!(
    tokenBalance &&
    currencyAmount &&
    (currencyAmount.lessThan(tokenBalance) || currencyAmount.equalTo(tokenBalance))
  )
  const [approval, approve] = useApproveCallback(currencyAmount, farm.address)

  const { lockTime, setLockTime } = useLockTimeValue(farm.lockTimeMin, farm.lockTimeMax)
  const multiplier = useLockTimeMultiplier(lockTime, farm)

  const estimate = useCallback(async () => {
    if (!currencyAmount || !wagmiPublicClient || !farm.address) return undefined
    const txn = {
      address: farm.address as Address,
      abi: KODIAK_FARM_ABI as Abi,
      functionName: 'stakeLocked',
      args: [BigInt(currencyAmount.quotient.toString()), BigInt(lockTime)],
    }
    return await wagmiPublicClient.estimateContractGas({ ...txn, account })
  }, [wagmiPublicClient, currencyAmount, lockTime, farm.address, account])

  const handleStake = useCallback(async () => {
    if (!currencyAmount || !wagmiPublicClient || !farm.address || !account || transactionState.attemptingTxn) return

    setTransactionState({
      attemptingTxn: true,
      errorMessage: undefined,
      txHash: undefined,
      transactionWaiting: false,
    })

    try {
      const txn = {
        address: farm.address as Address,
        abi: KODIAK_FARM_ABI as Abi,
        functionName: 'stakeLocked',
        args: [BigInt(currencyAmount.quotient.toString()), BigInt(lockTime)],
      }
      const gas = await wagmiPublicClient.estimateContractGas({ ...txn, account })
      const hash = await writeContractAsync({
        ...txn,
        gas: calculateGasMargin(gas as bigint) as bigint,
      })

      setTransactionState({
        attemptingTxn: false,
        errorMessage: undefined,
        txHash: hash,
        transactionWaiting: true,
      })

      addTransaction(
        { hash },
        {
          islandId: currencyAmount.currency.address,
          lockTime: lockTime.toString(),
          stakeAmount: currencyAmount.quotient.toString(),
          type: TransactionType.STAKE_LIQUIDITY_ISLAND,
        }
      )

      await waitForTransactionReceipt({ hash: hash as Hash })

      setTransactionState((prev) => ({
        ...prev,
        transactionWaiting: false,
      }))

      setShowConfirm(false)
      setAmount('')
    } catch (error: any) {
      console.error('Error staking:', error)
      setTransactionState({
        attemptingTxn: false,
        errorMessage: error?.message || 'Failed to stake',
        txHash: undefined,
        transactionWaiting: false,
      })
    }
  }, [
    currencyAmount,
    lockTime,
    farm.address,
    wagmiPublicClient,
    account,
    transactionState.attemptingTxn,
    writeContractAsync,
    addTransaction,
    waitForTransactionReceipt,
    setAmount,
  ])

  const ApproveButton = () => (
    <ButtonPrimary onClick={approve} disabled={approval === ApprovalState.PENDING} width="100%">
      {approval === ApprovalState.PENDING ? (
        <Dots>
          <>Approving {currency?.symbol}</>
        </Dots>
      ) : (
        <>Approve {currency?.symbol}</>
      )}
    </ButtonPrimary>
  )

  const onDeposit = () => {
    setShowConfirm(true)
  }

  const HeaderModal = () => {
    const { value } = useApiTokenValue(currencyAmount)
    const apr = useKodiakFarmApr(farm, multiplier)
    const earnings = useCalculationEarnings({
      farm,
      inputValue: amount,
      lockTimeValue: lockTime,
      multiplier,
    })
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const earningsValues = earnings.map((e) => useApiTokenValue(e).value)
    const earningsTotalValue = earningsValues.reduce((a, b) => (a || 0) + (b || 0), 0)

    return (
      <AutoColumn gap="4px" style={{ marginTop: '12px' }}>
        <LightCard padding="0.75rem 1rem">
          <AutoColumn gap="8px">
            <RowBetween align="center">
              <RowFixed gap="0px">
                <TruncatedText
                  fontSize={24}
                  fontWeight={500}
                  // color={showAcceptChanges && trade.tradeType === TradeType.EXACT_OUTPUT ? theme.deprecated_primary1 : ''}
                >
                  {currencyAmount?.toSignificant(8)}
                </TruncatedText>
              </RowFixed>
              <RowFixed gap="0px">
                <CurrencyLogo currency={currency} size="20px" style={{ marginRight: '12px' }} />
                <Text fontSize={20} fontWeight={500}>
                  {currencyAmount?.currency.symbol}
                </Text>
              </RowFixed>
            </RowBetween>
            <RowBetween>{value && formatMoney(value)}</RowBetween>
          </AutoColumn>
        </LightCard>
        <LightCard>
          <AutoColumn gap="8px">
            <RowBetween>
              <RowFixed>
                <MouseoverTooltip
                  text={
                    <>
                      The amount you expect to receive at the current market price. You may receive less or more if the
                      market price changes while your transaction is pending.
                    </>
                  }
                  disableHover={false}
                >
                  <ThemedText.DeprecatedSubHeader color={theme.deprecated_text1}>
                    Lock period
                  </ThemedText.DeprecatedSubHeader>
                </MouseoverTooltip>
              </RowFixed>
              <ThemedText.DeprecatedBlack textAlign="right" fontSize={14}>
                {lockTime / ONE_DAY} days
              </ThemedText.DeprecatedBlack>
            </RowBetween>
            <RowBetween>
              <RowFixed>
                <MouseoverTooltip
                  text={
                    <>
                      The amount you expect to receive at the current market price. You may receive less or more if the
                      market price changes while your transaction is pending.
                    </>
                  }
                  disableHover={false}
                >
                  <ThemedText.DeprecatedSubHeader color={theme.deprecated_text1}>APR</ThemedText.DeprecatedSubHeader>
                </MouseoverTooltip>
              </RowFixed>
              <ThemedText.DeprecatedBlack textAlign="right" fontSize={14}>
                {apr?.toFixed(2)}%
              </ThemedText.DeprecatedBlack>
            </RowBetween>
            <RowBetween>
              <RowFixed>
                <MouseoverTooltip
                  text={<>The impact your trade has on the market price of this pool.</>}
                  disableHover={false}
                >
                  <ThemedText.DeprecatedSubHeader color={theme.deprecated_text1}>
                    Multiplier
                  </ThemedText.DeprecatedSubHeader>
                </MouseoverTooltip>
              </RowFixed>
              <ThemedText.DeprecatedBlack textAlign="right" fontSize={14}>
                x{multiplier.toFixed(2)}
              </ThemedText.DeprecatedBlack>
            </RowBetween>
            <Separator />
            <RowBetween>
              <RowFixed style={{ marginRight: '20px', alignItems: 'end' }}>
                <MouseoverTooltip
                  text={
                    <>
                      The minimum amount you are guaranteed to receive. If the price slips any further, your transaction
                      will revert.
                    </>
                  }
                  disableHover={false}
                >
                  <ThemedText.DeprecatedSubHeader color={theme.deprecated_text3}>
                    Estimated Rewards
                  </ThemedText.DeprecatedSubHeader>
                </MouseoverTooltip>
              </RowFixed>
              <Column>
                {earnings.map((earn, index) => {
                  return (
                    <RowFixed key={index}>
                      <ThemedText.DeprecatedBlack textAlign="right" fontSize={14} color={theme.deprecated_text3}>
                        {earn?.toSignificant()} {earn?.currency.symbol} (<FiatValue value={earningsValues[index]} />)
                      </ThemedText.DeprecatedBlack>
                    </RowFixed>
                  )
                })}
              </Column>
            </RowBetween>
            {earnings.length > 1 && (
              <RowBetween>
                <RowFixed style={{ marginRight: '20px' }}>
                  <MouseoverTooltip text={<>Summary reward</>} disableHover={false}>
                    <ThemedText.DeprecatedSubHeader color={theme.deprecated_text3}>
                      Summary reward
                    </ThemedText.DeprecatedSubHeader>
                  </MouseoverTooltip>
                </RowFixed>
                <ThemedText.DeprecatedBlack textAlign="right" fontSize={14} color={theme.deprecated_text3}>
                  <FiatValue value={earningsTotalValue} />
                </ThemedText.DeprecatedBlack>
              </RowBetween>
            )}
          </AutoColumn>
        </LightCard>
      </AutoColumn>
    )
  }

  const BottomModal = () => {
    return (
      <ButtonPrimary
        style={{ marginTop: '12px' }}
        onClick={handleStake}
        disabled={
          !isValid ||
          approval !== ApprovalState.APPROVED ||
          transactionState.attemptingTxn ||
          transactionState.transactionWaiting
        }
      >
        <Text fontSize={20} fontWeight={500}>
          <>Confirm </>
        </Text>
      </ButtonPrimary>
    )
  }
  const DepositButton = () => (
    <ButtonPrimary onClick={onDeposit} disabled={!isValid || approval !== ApprovalState.APPROVED}>
      <Text fontSize={20} fontWeight={500}>
        <>Supply </>
      </Text>
    </ButtonPrimary>
  )

  return (
    <>
      <TransactionConfirmationModal
        isOpen={showConfirm}
        onDismiss={() => {
          setShowConfirm(false)
          if (!transactionState.attemptingTxn && !transactionState.transactionWaiting) {
            setTransactionState({
              attemptingTxn: false,
              errorMessage: undefined,
              txHash: undefined,
              transactionWaiting: false,
            })
          }
        }}
        attemptingTxn={transactionState.attemptingTxn}
        hash={transactionState.txHash}
        transactionWaiting={transactionState.transactionWaiting}
        content={() => (
          <ConfirmationModalContent
            title={<>You will stake</>}
            onDismiss={() => setShowConfirm(false)}
            topContent={HeaderModal}
            bottomContent={BottomModal}
          />
        )}
        pendingText="Pending"
        currencyToAdd={currency || undefined}
      />
      <CurrencyInputPanel
        value={amount}
        onUserInput={setAmount}
        onMax={() => tokenBalance && setAmount(tokenBalance.toExact())}
        showMaxButton={true}
        currency={currency}
        id="stake-input-token"
        showCommonBases
      />
      <LockTimePositionHeader>
        <div>Lock for {lockTime / ONE_DAY} day</div>
        <div>Multiplier: x{multiplier.toFixed(2)}</div>
      </LockTimePositionHeader>
      <Slider min={farm.lockTimeMin} max={farm.lockTimeMax} step={ONE_DAY} value={lockTime} onChange={setLockTime} />
      <EarningsCalculationView />
      {!account ? (
        <ButtonLight onClick={toggleWalletModal}>
          <>Connect Wallet</>
        </ButtonLight>
      ) : approval === ApprovalState.APPROVED || approval === ApprovalState.UNKNOWN ? (
        <DepositButton />
      ) : (
        <ApproveButton />
      )}
    </>
  )
}

export default DepositView
